<template>
  <div class="p-6 max-w-7xl mx-auto space-y-6">
    <!-- HEADER & STATS -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Receipt class="w-7 h-7 text-brand-500" />
          Gestor de Cobros & Verificación
        </h1>
        <p class="text-sm text-slate-500">
          Valida comprobantes de transferencias bancarias y aprueba el acceso mensual de tus clientes.
        </p>
      </div>

      <!-- Métricas Rápidas -->
      <div class="flex items-center gap-3">
        <div class="px-4 py-2 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-300 text-xs">
          <p class="font-medium text-[10px] uppercase tracking-wider">Por Verificar</p>
          <p class="text-lg font-black font-mono">{{ pendingProofsCount }}</p>
        </div>
        <div class="px-4 py-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs">
          <p class="font-medium text-[10px] uppercase tracking-wider">Total Aprobadas</p>
          <p class="text-lg font-black font-mono">{{ paidInvoicesCount }}</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-3">
      <button
        v-for="filter in filters"
        :key="filter.key"
        @click="currentFilter = filter.key"
        class="px-3 py-1.5 rounded-xl text-xs font-bold transition-all"
        :class="currentFilter === filter.key ? 'bg-brand-500 text-white shadow-xs' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-slate-300'"
      >
        {{ filter.label }}
      </button>
    </div>

    <!-- TABLA DE FACTURAS -->
    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-3xl overflow-hidden shadow-xs">
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead>
            <tr class="text-slate-400 border-b border-slate-100 dark:border-slate-700 uppercase tracking-wider font-bold">
              <th class="p-4">Factura / Cliente</th>
              <th class="p-4">Plan</th>
              <th class="p-4">Monto (USD)</th>
              <th class="p-4">Método & Ref.</th>
              <th class="p-4">Comprobante</th>
              <th class="p-4">Estado</th>
              <th class="p-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
            <tr v-if="filteredInvoices.length === 0">
              <td colspan="7" class="text-center py-8 text-slate-400">
                No hay facturas en esta categoría.
              </td>
            </tr>

            <tr v-for="inv in filteredInvoices" :key="inv.id" class="hover:bg-slate-50 dark:hover:bg-slate-700/30">
              <!-- Cliente -->
              <td class="p-4">
                <span class="font-mono font-black text-slate-900 dark:text-white block">#INV-{{ inv.id }}</span>
                <span class="font-bold text-slate-700 dark:text-slate-300">{{ inv.client_name }}</span>
                <span class="text-[11px] text-slate-400 block">{{ inv.client_email }}</span>
              </td>

              <!-- Plan -->
              <td class="p-4">
                <span class="font-semibold text-slate-800 dark:text-slate-200">{{ inv.plan_name }}</span>
                <span class="text-[10px] text-slate-400 block">{{ inv.period_start }} al {{ inv.period_end }}</span>
              </td>

              <!-- Monto -->
              <td class="p-4 font-black font-mono text-sm text-slate-900 dark:text-white">
                ${{ inv.amount_usd }}
              </td>

              <!-- Método y Referencia -->
              <td class="p-4">
                <span class="font-bold text-slate-700 dark:text-slate-300">{{ inv.payment_method || 'N/A' }}</span>
                <span v-if="inv.payment_reference" class="font-mono text-xs text-brand-600 dark:text-brand-400 block font-bold">
                  Ref: {{ inv.payment_reference }}
                </span>
              </td>

              <!-- Comprobante / Voucher -->
              <td class="p-4">
                <button
                  v-if="inv.proof_file_url"
                  @click="openProofPreview(inv.proof_file_url)"
                  class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 hover:bg-brand-500 hover:text-white transition-all font-semibold text-[11px]"
                >
                  <Eye class="w-3.5 h-3.5" /> Ver Voucher
                </button>
                <span v-else class="text-slate-400 italic text-[11px]">Sin archivo</span>
              </td>

              <!-- Estado -->
              <td class="p-4">
                <span
                  class="px-2.5 py-0.5 rounded-full text-[10px] font-bold"
                  :class="[
                    inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
                    inv.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400' :
                    inv.proof_file_url ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 animate-pulse' :
                    'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                  ]"
                >
                  {{ inv.status === 'paid' ? 'Aprobada' : (inv.status === 'rejected' ? 'Rechazada' : (inv.proof_file_url ? 'Por Validar' : 'Pendiente')) }}
                </span>
              </td>

              <!-- Acciones -->
              <td class="p-4 text-right">
                <div v-if="inv.status !== 'paid'" class="flex items-center justify-end gap-1.5">
                  <button
                    @click="approvePayment(inv.id)"
                    :disabled="isProcessing"
                    title="Aprobar Pago y Activar +30 Días"
                    class="p-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all shadow-xs"
                  >
                    <Check class="w-4 h-4" />
                  </button>

                  <button
                    v-if="inv.status !== 'rejected'"
                    @click="rejectPayment(inv.id)"
                    :disabled="isProcessing"
                    title="Rechazar Comprobante"
                    class="p-1.5 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-600 hover:text-white transition-all border border-red-500/20"
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>
                <span v-else class="text-emerald-600 font-bold text-[11px]">
                  Pagado el {{ formatDate(inv.paid_at) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL DE PREVISUALIZACIÓN DE COMPROBANTE -->
    <div v-if="previewUrl" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col space-y-4 shadow-2xl">
        <div class="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-3">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ExternalLink class="w-4 h-4 text-brand-500" /> Voucher de Transferencia
          </h3>
          <button @click="previewUrl = null" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="flex-1 overflow-auto rounded-2xl bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-2 min-h-[300px]">
          <iframe v-if="previewUrl.endsWith('.pdf')" :src="previewUrl" class="w-full h-[500px] rounded-xl"></iframe>
          <img v-else :src="previewUrl" alt="Voucher" class="max-h-[500px] object-contain rounded-xl shadow-md" />
        </div>

        <div class="flex justify-between items-center pt-2">
          <a :href="previewUrl" target="_blank" class="text-xs font-bold text-brand-500 hover:underline flex items-center gap-1">
            Abrir imagen original <ExternalLink class="w-3 h-3" />
          </a>
          <button @click="previewUrl = null" class="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-300">
            Cerrar
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Receipt, Eye, Check, X, ExternalLink } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';

const { showSuccess, showError } = useToast();

const invoices = ref([]);
const currentFilter = ref('pending_proof');
const isProcessing = ref(false);
const previewUrl = ref(null);

const filters = [
  { key: 'all', label: 'Todas las Facturas' },
  { key: 'pending_proof', label: 'Por Validar (Con Voucher)' },
  { key: 'pending_unpaid', label: 'Pendientes (Sin Subir)' },
  { key: 'paid', label: 'Aprobadas / Pagadas' },
  { key: 'rejected', label: 'Rechazadas' }
];

const fetchInvoices = async () => {
  try {
    const res = await apiClient.get('/admin_get_invoices');
    invoices.value = res.data?.data || [];
  } catch (err) {
    showError('Error al obtener la lista de facturas.');
  }
};

const pendingProofsCount = computed(() => {
  return invoices.value.filter(i => i.status === 'pending' && i.proof_file_url).length;
});

const paidInvoicesCount = computed(() => {
  return invoices.value.filter(i => i.status === 'paid').length;
});

const filteredInvoices = computed(() => {
  if (currentFilter.value === 'pending_proof') {
    return invoices.value.filter(i => i.status === 'pending' && i.proof_file_url);
  }
  if (currentFilter.value === 'pending_unpaid') {
    return invoices.value.filter(i => i.status === 'pending' && !i.proof_file_url);
  }
  if (currentFilter.value === 'paid') {
    return invoices.value.filter(i => i.status === 'paid');
  }
  if (currentFilter.value === 'rejected') {
    return invoices.value.filter(i => i.status === 'rejected');
  }
  return invoices.value;
});

const openProofPreview = (url) => {
  previewUrl.value = url;
};

const approvePayment = async (invoiceId) => {
  if (!confirm(`¿Confirmas que el pago de la factura #INV-${invoiceId} es válido? Se sumarán 30 días de suscripción al cliente.`)) return;

  isProcessing.value = true;
  try {
    const formData = new FormData();
    formData.append('invoice_id', invoiceId);
    const res = await apiClient.post('/approve_invoice', formData);
    showSuccess(res.data?.message || 'Factura aprobada y servicio extendido.');
    await fetchInvoices();
  } catch (err) {
    showError('Error al aprobar la factura.');
  } finally {
    isProcessing.value = false;
  }
};

const rejectPayment = async (invoiceId) => {
  if (!confirm(`¿Deseas rechazar el comprobante de la factura #INV-${invoiceId}?`)) return;

  isProcessing.value = true;
  try {
    const formData = new FormData();
    formData.append('invoice_id', invoiceId);
    const res = await apiClient.post('/admin_reject_invoice', formData);
    showSuccess(res.data?.message || 'Comprobante rechazado.');
    await fetchInvoices();
  } catch (err) {
    showError('Error al rechazar el comprobante.');
  } finally {
    isProcessing.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('es-NI', { day: '2-digit', month: 'short', year: 'numeric' });
};

onMounted(() => {
  fetchInvoices();
});
</script>